/**
 * Internal helpers extracted from useJournalEntryFormEffects to keep the hook
 * within size/complexity limits. These are not part of the public API of the
 * journal-entry-form page; they are imported only by the hook itself.
 */

import { type EntryLine } from './journal-entry-form-helpers'
import { listAccounts, type Account } from '../../services/account-service'
import { getExchangeRate } from '../../services/currency-service'
import {
  initializeLocalDb,
  getLocalAccounts,
} from '../../services/local-db'

// ── Account loading ──────────────────────────────────────────────────────────

export function filterPartyAccounts(allAccounts: Account[]): Account[] {
  // linkedPartyId is populated server-side via JOIN with party_ledger_assignments
  // and is the authoritative signal for "this account exists to back a party's
  // subsidiary ledger entry". Headers (chart-of-account groupings + control
  // accounts) are also excluded — pickers want only postable leaf accounts.
  return allAccounts.filter(a => !a.isHeader && !a.linkedPartyId)
}

function mapLocalAccountToAccount(a: Awaited<ReturnType<typeof getLocalAccounts>>[number]): Account {
  return {
    id: a.id,
    orgId: a.orgId,
    code: a.code,
    name: a.name,
    description: a.description,
    accountType: a.accountType,
    accountSubtype: a.accountSubtype,
    normalBalance: a.normalBalance,
    ...(a.parentId && { parentId: a.parentId }),
    path: a.path,
    depth: a.depth,
    isHeader: a.isHeader,
    isSystem: a.isSystem,
    isActive: a.isActive,
    isControl: a.isControl,
    ...(a.currency && { currency: a.currency }),
    audit: {
      createdAt: a.createdAt,
      updatedAt: a.updatedAt,
      createdBy: 'system',
      updatedBy: 'system',
    },
  }
}

export async function tryLoadAccountsFromApi(organizationId: string): Promise<Account[] | null> {
  try {
    const response = await listAccounts(organizationId, { includeInactive: false }, 1000)
    if (response.data && response.data.accounts.length > 0) {
      return filterPartyAccounts(response.data.accounts)
    }
    return null
  } catch {
    return null
  }
}

export async function loadAccountsFromLocalDb(organizationId: string): Promise<Account[]> {
  await initializeLocalDb(organizationId)
  const localAccounts = await getLocalAccounts(organizationId)
  const mapped = localAccounts
    .filter(a => !a.isHeader && a.isActive)
    .map(mapLocalAccountToAccount)
  return filterPartyAccounts(mapped)
}

// ── FX rate fetching ─────────────────────────────────────────────────────────

interface RateRefs {
  rateCache: { current: Record<string, number> }
  rateFetchingRef: { current: Set<string> }
}

function normalizeBaseLine(line: EntryLine): EntryLine | null {
  const debitVal = line.debit || ''
  const creditVal = line.credit || ''
  if (line.baseDebit === debitVal && line.baseCredit === creditVal) {return null}
  return { ...line, baseDebit: debitVal, baseCredit: creditVal, exchangeRate: undefined }
}

function normalizeCurrency(value: string | undefined): string {
  // Trim and upper-case before any comparison — a saved line that ended up
  // with " " or "" as currency (legacy data) would otherwise reach
  // getExchangeRate and 500 on the server side.
  return String(value ?? '').trim().toUpperCase()
}

function resolveExplicitRate(rateStr: string, currency: string, refs: RateRefs): number {
  const rate = parseFloat(rateStr)
  if (rate > 0 && !refs.rateCache.current[currency]) {
    refs.rateCache.current[currency] = rate
  }
  return rate
}

async function fetchRate(
  currency: string,
  baseCurrency: string,
  refs: RateRefs,
): Promise<number | undefined> {
  if (refs.rateFetchingRef.current.has(currency)) {return undefined}
  refs.rateFetchingRef.current.add(currency)
  try {
    const resp = await getExchangeRate(currency, baseCurrency)
    if (resp.data) {
      refs.rateCache.current[currency] = resp.data.rate
      return resp.data.rate
    }
    return undefined
  } catch {
    return undefined
  } finally {
    refs.rateFetchingRef.current.delete(currency)
  }
}

async function resolveRate(
  line: EntryLine,
  baseCurrency: string,
  refs: RateRefs,
): Promise<number | undefined> {
  const currency = normalizeCurrency(line.currency)
  if (!currency || currency === normalizeCurrency(baseCurrency)) {return undefined}
  if (line.exchangeRate) {
    return resolveExplicitRate(line.exchangeRate, currency, refs)
  }
  if (refs.rateCache.current[currency]) {
    return refs.rateCache.current[currency]
  }
  return fetchRate(currency, baseCurrency, refs)
}

function applyRateToLine(line: EntryLine, rate: number): EntryLine | null {
  const rateStr = String(rate)
  const debitAmt = parseFloat(line.debit || '0')
  const creditAmt = parseFloat(line.credit || '0')
  const newBaseDebit = debitAmt > 0 ? (debitAmt * rate).toFixed(2) : ''
  const newBaseCredit = creditAmt > 0 ? (creditAmt * rate).toFixed(2) : ''
  if (
    line.exchangeRate === rateStr &&
    line.baseDebit === newBaseDebit &&
    line.baseCredit === newBaseCredit
  ) {
    return null
  }
  return { ...line, exchangeRate: rateStr, baseDebit: newBaseDebit, baseCredit: newBaseCredit }
}

export interface FetchRatesContext {
  baseCurrency: string
  refs: RateRefs
  isCancelled: () => boolean
}

async function computeUpdatedLineForFx(
  line: EntryLine,
  ctx: FetchRatesContext,
): Promise<{ updated: EntryLine | null; isCancelled: boolean }> {
  if (!line.currency || line.currency === ctx.baseCurrency) {
    return { updated: normalizeBaseLine(line), isCancelled: false }
  }
  const amount = parseFloat(line.debit || line.credit || '0')
  if (amount === 0) {return { updated: null, isCancelled: false }}

  const rate = await resolveRate(line, ctx.baseCurrency, ctx.refs)
  if (ctx.isCancelled()) {return { updated: null, isCancelled: true }}
  if (!rate || rate <= 0) {return { updated: null, isCancelled: false }}
  return { updated: applyRateToLine(line, rate), isCancelled: false }
}

/**
 * Returns the updated lines and whether anything changed. Does not mutate
 * input. Returns `null` if the work was cancelled mid-flight.
 */
export async function computeFxUpdatedLines(
  lines: EntryLine[],
  ctx: FetchRatesContext,
): Promise<{ updatedLines: EntryLine[]; isChanged: boolean } | null> {
  const updatedLines = [...lines]
  let isChanged = false

  for (let i = 0; i < updatedLines.length; i++) {
    const line = updatedLines[i]
    if (!line) {continue}
    const { updated, isCancelled } = await computeUpdatedLineForFx(line, ctx)
    if (isCancelled) {return null}
    if (updated) { updatedLines[i] = updated; isChanged = true }
  }

  return { updatedLines, isChanged }
}
